import { useLayoutEffect, useRef, useState } from 'react';

import {
  cn,
  Table,
  TableBody,
  TableDensity,
  TableHead,
  TableHeader,
  TableRow,
} from '@crossnokaye/ux-common';

import { Sequence } from '@repo/sdk/models/components';

import { AddStageColumnHeader } from './add-stage-column';
import { ColumnOverlay, ColumnOverlayProperties } from './column-overlay';
import { RemoveStageButton } from './remove-stage-button';

interface SequenceEditorTableProps {
  /** The currently active stage index (1-based). Used for column highlighting. */
  activeStage?: number;
  /** Table row content rendered via composition pattern */
  children: React.ReactNode;
  /** Handler called when the "Add Stage" column is clicked. Required when showAddStageColumn is true. */
  onAddStageClicked?: () => void;
  /** Function to call when a stage is removed */
  onRemoveStageClicked: () => void;
  /** Optional overlay validation properties for each column */
  overlayProperties?: (ColumnOverlayProperties | null)[];
  /** The Sequence Data */
  sequence?: Sequence;
  /** Whether to show an "Add Stage" column on the right side of the table */
  showAddStageColumn?: boolean;
}

/**
 * SequenceEditorTable is a reusable table component for sequencer editors.
 * It uses a composition pattern where row content is passed as children.
 *
 * @example
 * // Basic usage
 * <SequenceEditorTable
 *   activeStage={currentStage}
 *   onRemoveStageClicked={handleRemoveStage}
 *   sequence={sequenceData}
 * >
 *   {rows.map((row) => (
 *     <TableRow key={row.id}>
 *       <EquipmentCell ... />
 *       {columns.map((col) => <SequenceCell ... />)}
 *     </TableRow>
 *   ))}
 * </SequenceEditorTable>
 *
 * @example
 * // With Add Stage column
 * <SequenceEditorTable
 *   activeStage={currentStage}
 *   onAddStageClicked={handleAddStage}
 *   onRemoveStageClicked={handleRemoveStage}
 *   sequence={sequenceData}
 *   showAddStageColumn
 * >
 *   {rows.map((row) => (
 *     <TableRow key={row.id}>
 *       <EquipmentCell ... />
 *       {columns.map((col) => <SequenceCell ... />)}
 *       <AddStageColumnCell />
 *     </TableRow>
 *   ))}
 * </SequenceEditorTable>
 */
export const SequenceEditorTable = ({
  activeStage,
  children,
  onAddStageClicked,
  onRemoveStageClicked,
  overlayProperties,
  sequence,
  showAddStageColumn = false,
}: SequenceEditorTableProps) => {
  // Refs for overlay positioning
  const columnHeaderRefs = useRef<(HTMLTableCellElement | null)[]>([]);

  const maxStages = Math.max(
    ...(sequence?.table?.map((seq: any) => seq.stageValues.length - 1) || [])
  );
  const columnTitles = Array.from({ length: maxStages }, (_, i) => `Stage ${i + 1}`);

  // Track when refs are ready to trigger overlay re-positioning
  const [, forceUpdate] = useState({});

  // Force re-render after refs are set on initial render or when columns change
  useLayoutEffect(() => {
    // Force update after DOM has been mutated but before paint
    forceUpdate({});
  }, [columnTitles.length]);

  if (!sequence) {
    return null;
  }

  return (
    <div className="overflow-x-auto relative pb-6">
      <Table className={'w-auto  border-separate'} density={TableDensity.COMFORTABLE}>
        <TableHeader>
          <TableRow>
            <TableHead
              ref={(el) => {
                columnHeaderRefs.current[0] = el;
              }}
              className={cn('text-left relative w-fit border-b-1')}
              key={'equipment_head'}
            >
              {'Equipment'}
            </TableHead>
            {columnTitles.map((column: string, index: number) => (
              <TableHead
                ref={(el) => {
                  columnHeaderRefs.current[index + 1] = el;
                }}
                className={cn(
                  'text-left relative w-24 border-b-1 min-w-24',
                  activeStage === index + 1 ? 'bg-ckds-base-white text-muted' : ''
                )}
                key={column + 'head'}
              >
                {column}
                {index === columnTitles.length - 1 ? (
                  <RemoveStageButton onClick={onRemoveStageClicked} />
                ) : null}
              </TableHead>
            ))}
            {showAddStageColumn && onAddStageClicked && (
              <AddStageColumnHeader onClick={onAddStageClicked} />
            )}
          </TableRow>
        </TableHeader>
        <TableBody>{children}</TableBody>
      </Table>
      {/* Render overlay if validation exists for this column */}
      {columnTitles.map((_: string, index: number) => {
        return (
          <ColumnOverlay
            key={`overlay-${index}`}
            columnIndex={index + 1}
            overlayProperty={overlayProperties?.[index + 1] || undefined}
            targetElement={columnHeaderRefs.current?.[index + 1] || null}
          />
        );
      })}
    </div>
  );
};
